import { Checkbox, ListItemText } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import { useObserver } from 'mobx-react-lite';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import WindsorApi from '../../api/WindsorApi';
import UserContext from '../../auth/UserContext';
import LoadingIndicator from '../../common/LoadingIndicator';
import NoResultsText from '../../common/NoResultsText';
import WorkflowsTable from './WorkflowsTable';
import { RouteComponentProps, withRouter } from 'react-router-dom';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textFieldInput: {
      paddingTop: 12,
      paddingBottom: 12
    },
    toolbar: {
      marginBottom: 16,
      display: 'flex',
      justifyContent: 'space-between'
    },
    filtersContainer: {
      marginLeft: 8,
      display: 'flex'
    },
    selectInput: {
      paddingTop: 12,
      paddingBottom: 12
    },
    accountFilter: {
      marginRight: 8
    },
    searchField: {
      width: 300
    }
  })
);

interface Props {
  familyId: string;
}

const windsorApi = new WindsorApi();

function Workflows(props: Props & RouteComponentProps<any> & WithSnackbarProps) {
  const { familyId, enqueueSnackbar, history } = props;
  const classes = useStyles({});
  const [workflows, setWorkflows] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filters, setFilters] = useState({ account: [], region: [] });
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [workflowToArchive, setWorkflowToArchive] = useState(null);
  const [page, setPage] = useState(0);
  const user = useContext(UserContext);

  useEffect(() => {
    windsorApi.getWorkflows(familyId).then(workflows => {
      setWorkflows(workflows.map(workflow => {
        let customTime;
        if (!workflow.EnableScheduledTests) {
          customTime = "None";
        } else if (workflow.EnableScheduledTests && !workflow.DailyScheduledExecutionTime) {
          customTime = "2:00 UTC";
        } else {
          customTime = workflow.DailyScheduledExecutionTime + " UTC";
        }

        return {
          id: workflow.GUID,
          name: workflow.Name,
          account: workflow.AccountName,
          region: workflow.Region,
          lastModified: workflow.LastModified * 1000,
          scheduled: customTime,
          archived: workflow.Archived,
          subscribed: !!workflow.Subscribers && workflow.Subscribers.includes(user.email)
        };
      }));
    });
  }, [familyId, user.email]);

  useEffect(() => {
    if (workflows) {
      const { accounts, regions } = workflows.reduce((accumulator, workflow) => {
        accumulator.accounts.add(workflow.account);
        accumulator.regions.add(workflow.region);
        return accumulator;
      }, { accounts: new Set(), regions: new Set() });
      setAccounts(Array.from(accounts));
      setRegions(Array.from(regions));
    }
  }, [workflows]);

  function onPageChange(newPage: number) {
    setPage(newPage);
  }

  function onSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearch(event.target.value);
    // Reset page
    setPage(0);
  }

  function onToggleSubscription(workflow: any) {
    const subscribe = !workflow.subscribed;
    const workflowsCopy = workflows.slice();
    const index = workflows.findIndex(({ id }) => id === workflow.id);
    if (index !== -1) {
      workflowsCopy[index] = { ...workflows[index], subscribed: subscribe };
      setWorkflows(workflowsCopy);
    }
    windsorApi.updateWorkflowSubscription(subscribe, workflow.id, familyId);
    enqueueSnackbar(subscribe ? 'Subscription added' : 'Subscription removed');
  }

  function onToggleArchiveState() {
    const archive = !workflowToArchive.archived;
    const workflowsCopy = workflows.slice();
    const index = workflows.findIndex(({ id }) => id === workflowToArchive.id);
    if (index !== -1) {
      workflowsCopy[index] = { ...workflows[index], archived: archive };
      setWorkflows(workflowsCopy);
    }
    if (archive) {
      windsorApi.archiveWorkflow(workflowToArchive.id, familyId);
    } else {
      windsorApi.unarchiveWorkflow(workflowToArchive.id, familyId);
    }
    enqueueSnackbar(archive ? 'Workfow archived' : 'Workflow unarchived');
    setWorkflowToArchive(null);
  }

  function onFilterChanged(event: React.ChangeEvent<{ name?: string; value: unknown }>) {
    setFilters({ ...filters, [event.target.name as string]: event.target.value });
    // Reset page
    setPage(0);
  }

  function onToggleShowArchived(event: React.ChangeEvent<HTMLInputElement>) {
    setShowArchived(event.target.checked);
  }

  function onOpenArchiveConfirmationDialog(workflow: any) {
    setWorkflowToArchive(workflow);
  }

  function onCloseArchiveConfirmationDialog() {
    setWorkflowToArchive(null);
  }

  function onExecuteWorkflow(workflow: any) {
    history.push(`/families/${familyId}/workflows/${workflow.id}/execute`);
  }

  const normalizedSearch = search.toLowerCase();
  const hasFilter = Object.keys(filters).some(key => filters[key].length);

  const filteredWorkflows = workflows ? workflows.filter(workflow => {
    if (normalizedSearch && !workflow.name.toLowerCase().includes(normalizedSearch)) {
      return false;
    }

    if (!showArchived && workflow.archived) {
      return false;
    }

    if (hasFilter && !Object.keys(filters).every(key => !filters[key].length || filters[key].includes(workflow[key]))) {
      return false;
    }

    return true;
  }) : null;

  return useObserver(() => (
    <div>
      <div className={classes.toolbar}>
        <TextField
          id="workflow-search"
          className={classes.searchField}
          value={search}
          autoComplete="off"
          margin="none"
          variant="outlined"
          placeholder="Search by workflow name"
          inputProps={{ 'aria-label': 'bare', className: classes.textFieldInput }}
          onChange={onSearchChange}
        />
        <div className={classes.filtersContainer}>
          <Tooltip title="Show archived workflows">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showArchived}
                  onChange={onToggleShowArchived}
                  value="showArchived"
                  color="primary"
                />
              }
              label="Show Archived"
            />
          </Tooltip>
          <Select
            value={filters.account}
            className={classes.accountFilter}
            displayEmpty
            onChange={onFilterChanged}
            renderValue={() => 'Account'}
            multiple
            input={<OutlinedInput
              labelWidth={0}
              name="account"
              classes={{
                input: classes.selectInput
              }}
            />}
          >
            {accounts.map(account => <MenuItem key={account} value={account}>
              <Checkbox checked={filters.account.includes(account)} color="primary" />
              <ListItemText primary={account} />
            </MenuItem>)}
          </Select>
          <Select
            value={filters.region}
            displayEmpty
            onChange={onFilterChanged}
            renderValue={() => 'Region'}
            multiple
            input={<OutlinedInput
              labelWidth={0}
              name="region"
              classes={{
                input: classes.selectInput
              }}
            />}
          >
            {regions.map(region => <MenuItem key={region} value={region}>
              <Checkbox checked={filters.region.includes(region)} color="primary" />
              <ListItemText primary={region} />
            </MenuItem>)}
          </Select>
        </div>
      </div>
      {filteredWorkflows && filteredWorkflows.length > 0 && <Paper>
        <WorkflowsTable
          familyId={familyId}
          workflows={filteredWorkflows}
          page={page}
          showArchivedColumn={showArchived}
          onToggleArchiveState={onOpenArchiveConfirmationDialog}
          onExecuteWorkflow={onExecuteWorkflow}
          onToggleSubscription={onToggleSubscription}
          onPageChange={onPageChange}
        />
      </Paper>}
      {filteredWorkflows && filteredWorkflows.length === 0 && !hasFilter && <NoResultsText>No workflows</NoResultsText>}
      {filteredWorkflows && filteredWorkflows.length === 0 && hasFilter && <NoResultsText>No workflows matching filters</NoResultsText>}
      <LoadingIndicator loading={!filteredWorkflows} />
      {workflowToArchive && <Dialog
        open={true}
        onClose={onCloseArchiveConfirmationDialog}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        <DialogTitle id="confirmation-dialog-title">{`Are you sure you want to ${workflowToArchive.archived ? 'unarchive' : 'archive'} this workflow?`}</DialogTitle>
        <DialogContent>
          {workflowToArchive && <DialogContentText id="confirmation-dialog-description">
            The <strong>{workflowToArchive.name}</strong> workflow will be {workflowToArchive.archived ? 'unarchived' : 'archived'}.
          </DialogContentText>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseArchiveConfirmationDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={onToggleArchiveState} color="primary" autoFocus>
            {workflowToArchive.archived ? 'Unarchive' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>}
    </div>
  ));
};

export default withRouter(withSnackbar(Workflows));
